// src/components/PrivacyPolicy.jsx
import React, { useEffect } from 'react';
import './LegalPages.css'; // Using the same CSS as Terms & Conditions

function PrivacyPolicy() {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const currentDate = new Date().toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
    });

    return (
        <div className="legal-page">
            <div className="legal-container">
                <h1>Privacy Policy</h1>
                <p className="last-updated">Last Updated: {currentDate}</p>

                {/* Introduction */}
                <section>
                    <h2>1. Introduction</h2>
                    <p>
                        Welcome to Koru AI. We are committed to protecting your personal information and 
                        your right to privacy. This Privacy Policy explains how we collect, use, disclose, 
                        and safeguard your information when you visit our website and use our services.
                    </p>
                    <p>
                        Please read this privacy policy carefully. If you do not agree with the terms 
                        of this privacy policy, please do not access the site or use our services.
                    </p>
                </section>

                {/* Information We Collect */}
                <section>
                    <h2>2. Information We Collect</h2>
                    
                    <h3>2.1 Personal Information You Disclose to Us</h3>
                    <p>We collect personal information that you voluntarily provide to us when you:</p>
                    <ul>
                        <li>Submit a contact form</li>
                        <li>Subscribe to our newsletter</li>
                        <li>Create an account (if applicable)</li>
                        <li>Request support or assistance</li>
                    </ul>
                    
                    <p>The personal information we collect may include:</p>
                    <ul>
                        <li>Your name</li>
                        <li>Email address</li>
                        <li>Phone number (if provided)</li>
                        <li>Any other information you choose to provide</li>
                    </ul>

                    <h3>2.2 Information Automatically Collected</h3>
                    <p>When you visit our website, we automatically collect certain information about your device, including:</p>
                    <ul>
                        <li>IP address</li>
                        <li>Browser type and version</li>
                        <li>Time zone setting</li>
                        <li>Browser plug-in types and versions</li>
                        <li>Operating system and platform</li>
                        <li>Pages you view and how you interact with our site</li>
                    </ul>
                    <p>
                        <strong>How this happens automatically:</strong> Your browser sends this data with every HTTP request. 
                        Web servers (like Vercel/Render) automatically log this information. You don't need to add any code - 
                        it's built into the internet's infrastructure!
                    </p>
                </section>

                {/* How We Use Your Information */}
                <section>
                    <h2>3. How We Use Your Information</h2>
                    <p>We use the information we collect to:</p>
                    <ul>
                        <li>Respond to your inquiries and fulfill your requests</li>
                        <li>Send you newsletters and updates (with your consent)</li>
                        <li>Improve and optimize our website and services</li>
                        <li>Analyze usage patterns and trends</li>
                        <li>Protect against fraudulent or unauthorized activity</li>
                        <li>Comply with legal obligations</li>
                    </ul>
                </section>

                {/* Cookies and Tracking */}
                <section>
                    <h2>4. Cookies and Tracking Technologies</h2>
                    <p>
                        We use cookies and similar tracking technologies to track activity on our website 
                        and hold certain information. Cookies are files with a small amount of data which 
                        may include an anonymous unique identifier.
                    </p>
                    <p>We use the following types of cookies:</p>
                    <ul>
                        <li>
                            <strong>Essential Cookies:</strong> Necessary for the website to function 
                            properly and cannot be switched off.
                        </li>
                        <li>
                            <strong>Functional Cookies:</strong> Enable enhanced functionality and 
                            personalization.
                        </li>
                        <li>
                            <strong>Analytics Cookies:</strong> Help us understand how visitors interact 
                            with our website.
                        </li>
                    </ul>
                    <p>
                        You can instruct your browser to refuse all cookies or to indicate when a cookie 
                        is being sent. However, if you do not accept cookies, you may not be able to use 
                        some portions of our website.
                    </p>
                    <p>
                        For more detailed information about cookies, please see our <a href="/cookies">Cookies Policy</a>.
                    </p>
                </section>

                {/* Third-Party Services */}
                <section>
                    <h2>5. Third-Party Services</h2>
                    <p>We may use third-party service providers to:</p>
                    <ul>
                        <li>Host our website and data</li>
                        <li>Send email communications</li>
                        <li>Analyze website traffic and usage</li>
                        <li>Process payments (if applicable)</li>
                    </ul>
                    <p>
                        These third parties have access to your personal information only to perform specific 
                        tasks on our behalf and are obligated not to disclose or use it for any other purpose.
                    </p>
                    <p>We currently use the following third-party services:</p>
                    <ul>
                        <li><strong>Vercel/Render:</strong> For web hosting and deployment</li>
                        <li><strong>Kaggle:</strong> For AI tools dataset</li>
                        <li><strong>SMTP Service:</strong> For sending contact form emails</li>
                    </ul>
                </section>

                {/* Data Storage and Security */}
                <section>
                    <h2>6. Data Storage and Security</h2>
                    <p>
                        We implement appropriate technical and organizational security measures to protect 
                        your personal information. However, please remember that no method of transmission 
                        over the Internet or method of electronic storage is 100% secure.
                    </p>
                    <p>Our data practices include:</p>
                    <ul>
                        <li>Data stored in secure server environments</li>
                        <li>Regular security updates and monitoring</li>
                        <li>Limited access to personal information</li>
                        <li>Encryption of sensitive data where appropriate</li>
                    </ul>
                    <p>
                        Your data is stored on secure servers provided by Vercel/Render. Additionally, 
                        tool data is cached locally in your browser using IndexedDB for faster performance.
                    </p>
                </section>

                {/* Data Retention */}
                <section>
                    <h2>7. Data Retention</h2>
                    <p>
                        We will retain your personal information only for as long as necessary to fulfill 
                        the purposes outlined in this Privacy Policy, unless a longer retention period is 
                        required or permitted by law.
                    </p>
                    <p>
                        Contact form submissions are retained for customer service purposes and are not 
                        used for marketing purposes without your explicit consent.
                    </p>
                    <p>
                        Cached tool data in your browser's IndexedDB is stored locally and can be cleared 
                        at any time by clearing your browser data.
                    </p>
                </section>

                {/* Your Rights */}
                <section>
                    <h2>8. Your Privacy Rights</h2>
                    <p>Depending on your location, you may have the following rights:</p>
                    <ul>
                        <li>
                            <strong>Access:</strong> Request a copy of the personal information we hold about you
                        </li>
                        <li>
                            <strong>Correction:</strong> Request correction of inaccurate or incomplete information
                        </li>
                        <li>
                            <strong>Deletion:</strong> Request deletion of your personal information
                        </li>
                        <li>
                            <strong>Opt-out:</strong> Opt-out of marketing communications
                        </li>
                        <li>
                            <strong>Data Portability:</strong> Request transfer of your data to another service
                        </li>
                    </ul>
                    <p>
                        To exercise any of these rights, please contact us using the information provided 
                        in the "Contact Us" section below.
                    </p>
                </section>

                {/* Children's Privacy */}
                <section>
                    <h2>9. Children's Privacy</h2>
                    <p>
                        Our website is not intended for children under 13 years of age. We do not knowingly 
                        collect personal information from children under 13. If you are a parent or guardian 
                        and believe your child has provided us with personal information, please contact us.
                    </p>
                </section>

                {/* International Data Transfers */}
                <section>
                    <h2>10. International Data Transfers</h2>
                    <p>
                        Your information may be transferred to and maintained on computers located outside 
                        of your state, province, country, or other governmental jurisdiction where data 
                        protection laws may differ. By using our website, you consent to this transfer.
                    </p>
                </section>

                {/* Changes to This Privacy Policy */}
                <section>
                    <h2>11. Changes to This Privacy Policy</h2>
                    <p>
                        We may update our Privacy Policy from time to time. We will notify you of any changes 
                        by posting the new Privacy Policy on this page and updating the "Last Updated" date 
                        at the top.
                    </p>
                    <p>
                        You are advised to review this Privacy Policy periodically for any changes. Changes 
                        to this Privacy Policy are effective when they are posted on this page.
                    </p>
                </section>

                {/* Contact Us */}
                <section>
                    <h2>12. Contact Us</h2>
                    <p>If you have any questions about this Privacy Policy or our data practices, please contact us at:</p>
                    <ul>
                        <li><strong>Email:</strong> c84289798@gmail.com</li>
                        <li><strong>Through our website:</strong> Use the contact form on our website</li>
                        <li><strong>Response Time:</strong> We will respond to your inquiry within 5-7 business days</li>
                    </ul>
                </section>

                {/* Your Consent */}
                <section>
                    <h2>13. Your Consent</h2>
                    <p>
                        By using our website, you consent to our Privacy Policy and agree to its terms.
                    </p>
                </section>

                {/* Last Section */}
                <section>
                    <h2>14. Acknowledgment</h2>
                    <p>
                        This Privacy Policy was last updated on {currentDate}. We encourage you to review 
                        this page periodically for any updates or changes. Your continued use of our website 
                        after any changes indicates your acceptance of the updated policy.
                    </p>
                </section>
            </div>
        </div>
    );
}

export default PrivacyPolicy;